import store, { AppState } from '../../state';
import * as SunCalc from 'suncalc';
import {
  getMoonPhaseDetails,
  getGalacticCorePosition,
  getGalacticCoreDayTimeline,
  formatHourDecimal,
  isMoonlessNight
} from '../../utils/astronomy';
import { SolarLunarService, getUtcDateForLocalTime } from '../../services/SolarLunarService';

export interface SunMoonArHudCallbacks {
  onExit: () => void;
  onCenterTarget: () => void;
  onZoomToMarker: () => void;
  onTargetModeChange?: (mode: 'sun' | 'moon') => void;
  onPerspectiveModeChange?: (mode: 'first-person' | 'observer') => void;
  onFocalLengthChange?: (focalLengthMm: number) => void;
  onDateChange: (newDate: Date) => void;
  onTimeChange: (hourDecimal: number) => void;
  onAimMilkyWay?: () => void;
}

export class SunMoonArHud {
  public mode: 'sun-moon' | 'milky-way' = 'sun-moon';
  private container: HTMLElement | null = null;
  private rootEl: HTMLElement | null = null;
  private calendarPopupEl: HTMLElement | null = null;
  private callbacks: SunMoonArHudCallbacks | null = null;
  private isCalendarOpen: boolean = false;
  private isDockCollapsed: boolean = false;
  private calendarMonthOffset: number = 0; // 0 to 11 months from today
  private savedTimelineDisplay?: string;
  private savedTrendCollapsed?: boolean;

  public mount(container: HTMLElement, callbacks: SunMoonArHudCallbacks, mode: 'sun-moon' | 'milky-way' = 'sun-moon') {
    this.mode = mode;
    this.container = container;
    this.callbacks = callbacks;

    // Replace master timeline and collapse trend chart to maximize screen real estate
    const timelineContainer = document.getElementById('timeline-container');
    if (timelineContainer) {
      this.savedTimelineDisplay = timelineContainer.style.display;
      timelineContainer.style.display = 'none';
    }

    const currentState = store.getState();
    this.savedTrendCollapsed = currentState.dockedTrendCollapsed;
    if (!currentState.dockedTrendCollapsed) {
      store.update({ dockedTrendCollapsed: true });
    }

    this.render();
  }

  public unmount() {
    if (this.rootEl && this.rootEl.parentNode) {
      this.rootEl.parentNode.removeChild(this.rootEl);
    }
    this.rootEl = null;
    this.container = null;
    this.callbacks = null;
    this.isCalendarOpen = false;

    // Restore master timeline
    const timelineContainer = document.getElementById('timeline-container');
    if (timelineContainer) {
      timelineContainer.style.display = this.savedTimelineDisplay !== undefined ? this.savedTimelineDisplay : '';
    }

    // Restore trend chart collapse state
    if (this.savedTrendCollapsed !== undefined) {
      store.update({ dockedTrendCollapsed: this.savedTrendCollapsed });
      this.savedTrendCollapsed = undefined;
    }
  }

  public update(state: AppState) {
    if (!this.rootEl) return;
    this.updateTelemetry(state);
    this.updateDateDisplay(state);
    this.updateTimeDisplay(state);
    this.updatePerspectiveSelection(state);
    this.updateLensSelection(state);
    this.updateTargetModeToggle(state);
    this.renderQuickPresets(state);
    if (this.mode === 'milky-way') {
      this.updateMilkyWayTimeline(state);
      this.updateMilkyWayMoonDisplay(state);
    }
    if (this.isCalendarOpen) {
      this.renderCalendarContent(state);
    }
  }

  private render() {
    if (!this.container) return;

    // Remove existing if any
    const existing = document.getElementById('sun-moon-ar-hud-root');
    if (existing) existing.remove();

    const root = document.createElement('div');
    root.id = 'sun-moon-ar-hud-root';
    root.style.position = 'absolute';
    root.style.top = '0';
    root.style.left = '0';
    root.style.width = '100%';
    root.style.height = '100%';
    root.style.pointerEvents = 'none';
    root.style.zIndex = '10000';
    root.style.display = 'flex';
    root.style.flexDirection = 'column';
    root.style.justifyContent = 'space-between';
    root.style.padding = '16px 16px 0 16px';
    root.style.boxSizing = 'border-box';
    root.style.fontFamily = 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';

    const state = store.getState();

    root.innerHTML = `
      <!-- TOP BAR HUD -->
      <div id="ar-top-hud" style="pointer-events: auto; display: flex; align-items: flex-start; justify-content: flex-start; width: 100%;">
        <!-- Left: Exit Button and Guide Help Button -->
        <div style="display: flex; align-items: center; gap: 8px; margin-left: 64px;">
          <button id="btn-ar-exit" style="display: flex; align-items: center; gap: 6px; padding: 9px 18px; background: rgba(15, 23, 42, 0.85); backdrop-filter: blur(12px); -webkit-backdrop-filter: blur(12px); border: 1px solid rgba(255, 255, 255, 0.15); color: #f8fafc; font-weight: 700; font-size: 0.85rem; border-radius: 9999px; cursor: pointer; box-shadow: 0 4px 15px rgba(0,0,0,0.3); transition: all 0.2s ease;">
            <span>◀ Exit 3D View</span>
          </button>
          <button id="btn-ar-help" title="View 3D AR Guide & Tips" style="display: flex; align-items: center; justify-content: center; width: 36px; height: 36px; background: rgba(15, 23, 42, 0.85); backdrop-filter: blur(12px); -webkit-backdrop-filter: blur(12px); border: 1px solid rgba(56, 189, 248, 0.3); color: #38bdf8; font-weight: 700; font-size: 0.95rem; border-radius: 50%; cursor: pointer; box-shadow: 0 4px 15px rgba(0,0,0,0.3); transition: all 0.2s ease;">
            <span>ℹ️</span>
          </button>
        </div>
      </div>

      <!-- CENTER FLOATING HUD: Telemetry & Controls floating under the search bar -->
      <div id="ar-center-hud" style="position: absolute; top: 54px; left: 50%; transform: translateX(-50%); display: flex; align-items: center; gap: 10px; z-index: 10001; pointer-events: auto;">
        <!-- Telemetry Pill -->
        <div id="ar-telemetry-badge" style="display: flex; align-items: center; gap: 6px; padding: 7px 16px; background: rgba(15, 23, 42, 0.90); backdrop-filter: blur(12px); -webkit-backdrop-filter: blur(12px); border: 1px solid rgba(249, 115, 22, 0.4); border-radius: 9999px; font-size: 0.8rem; font-weight: 700; color: #f8fafc; box-shadow: 0 4px 15px rgba(0,0,0,0.4); white-space: nowrap;">
          <span id="ar-telemetry-icon">${this.mode === 'milky-way' ? '🌌' : '☀️'}</span>
          <span id="ar-telemetry-text">Calculating...</span>
        </div>

        <!-- Controls: Either Milky Way Aim OR Sun/Moon Target Toggle -->
        <div style="display: flex; align-items: center; gap: 6px; background: rgba(15, 23, 42, 0.90); backdrop-filter: blur(12px); -webkit-backdrop-filter: blur(12px); padding: 4px 8px; border-radius: 9999px; border: 1px solid rgba(255, 255, 255, 0.18); box-shadow: 0 4px 15px rgba(0,0,0,0.4); white-space: nowrap;">
          ${this.mode === 'milky-way' ? `
            <button id="btn-ar-aim-mw" title="Aim Camera at Milky Way Galactic Core" style="display: flex; align-items: center; gap: 5px; padding: 6px 14px; background: linear-gradient(135deg, #6366f1 0%, #a855f7 100%); border: 1px solid rgba(255, 255, 255, 0.25); border-radius: 9999px; color: #ffffff; font-size: 0.8rem; font-weight: 700; cursor: pointer; transition: all 0.2s ease; box-shadow: 0 2px 10px rgba(99, 102, 241, 0.4);">
              <span>🎯 Aim towards Milky Way</span>
            </button>
          ` : `
            <div style="display: flex; background: rgba(255, 255, 255, 0.08); border-radius: 9999px; padding: 2px;">
              <button id="btn-ar-mode-sun" class="ar-mode-btn ${state.arTargetMode === 'sun' ? 'active' : ''}" style="padding: 5px 12px; border: none; border-radius: 9999px; font-size: 0.78rem; font-weight: 700; cursor: pointer; transition: all 0.2s ease; ${state.arTargetMode === 'sun' ? 'background: #f97316; color: #fff; box-shadow: 0 2px 8px rgba(249, 115, 22, 0.4);' : 'background: transparent; color: #94a3b8;'}">
                ☀️ Sun
              </button>
              <button id="btn-ar-mode-moon" class="ar-mode-btn ${state.arTargetMode === 'moon' ? 'active' : ''}" style="padding: 5px 12px; border: none; border-radius: 9999px; font-size: 0.78rem; font-weight: 700; cursor: pointer; transition: all 0.2s ease; ${state.arTargetMode === 'moon' ? 'background: #38bdf8; color: #0f172a; box-shadow: 0 2px 8px rgba(56, 189, 248, 0.4);' : 'background: transparent; color: #94a3b8;'}">
                🌙 Moon
              </button>
            </div>

            <button id="btn-ar-recenter" title="Aim camera at target" style="display: flex; align-items: center; gap: 4px; padding: 5px 10px; background: rgba(255, 255, 255, 0.12); border: 1px solid rgba(255, 255, 255, 0.15); border-radius: 9999px; color: #f8fafc; font-size: 0.75rem; font-weight: 600; cursor: pointer; transition: all 0.2s ease;">
              🎯 Aim Camera
            </button>
          `}

          <button id="btn-ar-zoom-marker" title="Zoom camera to selected marker" style="display: flex; align-items: center; gap: 4px; padding: 5px 12px; background: rgba(56, 189, 248, 0.15); border: 1px solid rgba(56, 189, 248, 0.35); border-radius: 9999px; color: #38bdf8; font-size: 0.75rem; font-weight: 600; cursor: pointer; transition: all 0.2s ease;">
            📍 Zoom to Marker
          </button>
        </div>
      </div>

      <!-- 365-DAY MINI CALENDAR POPUP -->
      <div id="ar-calendar-popup" style="display: none; pointer-events: auto; position: absolute; bottom: 240px; left: 50%; transform: translateX(-50%); width: 340px; background: rgba(15, 23, 42, 0.95); backdrop-filter: blur(16px); -webkit-backdrop-filter: blur(16px); border: 1px solid rgba(255, 255, 255, 0.18); border-radius: 14px; padding: 14px; box-shadow: 0 10px 35px rgba(0,0,0,0.6); z-index: 10002;">
        <!-- Calendar content populated dynamically -->
      </div>

      <!-- FIRST TIME ONBOARDING / GUIDE MODAL -->
      <div id="ar-guide-modal" style="display: none; pointer-events: auto; position: absolute; top: 0; left: 0; width: 100%; height: 100%; background: rgba(0, 0, 0, 0.65); backdrop-filter: blur(8px); -webkit-backdrop-filter: blur(8px); z-index: 10005; align-items: center; justify-content: center; padding: 16px; box-sizing: border-box;">
        <div style="background: rgba(15, 23, 42, 0.96); border: 1px solid rgba(56, 189, 248, 0.35); border-radius: 18px; max-width: 540px; width: 100%; padding: 22px; box-shadow: 0 20px 50px rgba(0,0,0,0.8); display: flex; flex-direction: column; gap: 14px; color: #f8fafc;">
          <div style="display: flex; justify-content: space-between; align-items: center; border-bottom: 1px solid rgba(255,255,255,0.1); padding-bottom: 10px;">
            <div style="display: flex; align-items: center; gap: 8px;">
              <span style="font-size: 1.3rem;">${this.mode === 'milky-way' ? '🌌' : '☀️🌙'}</span>
              <h2 style="font-size: 1.05rem; font-weight: 700; color: #f8fafc; margin: 0;">
                ${this.mode === 'milky-way' ? 'Milky Way 3D AR Astrophotography' : '3D Sun & Moon AR View'}
              </h2>
            </div>
            <button id="btn-ar-guide-close" style="background: transparent; border: none; color: #94a3b8; font-size: 1.4rem; cursor: pointer; line-height: 1; padding: 0 4px;">&times;</button>
          </div>

          <div style="display: flex; flex-direction: column; gap: 12px; font-size: 0.84rem; line-height: 1.55; color: #cbd5e1;">
            ${this.mode === 'milky-way' ? `
              <p style="margin: 0;">
                The <strong>Milky Way 3D AR View</strong> lets you simulate the position of the Galactic Core and the arch of the Milky Way against real 3D mountain topography.
              </p>
              <p style="margin: 0;">
                Use the <strong>24-hour horizon track</strong> and time slider to see when the Galactic Core will rise above the horizon and whether darkness conditions are optimal for night sky photography.
              </p>
              <p style="margin: 0;">
                Click <strong>🎯 Aim towards Milky Way</strong> to automatically face the core in 3D perspective.
              </p>
            ` : `
              <p style="margin: 0;">
                The 3D Sun/Moon AR view allows the user to see the sun and moon position throughout the year from a first-person perspective. You can slide the time slider through the day to see how light will hit the mountains, where the rise/set position will be so you can plan where to take photos, setup camp, and know whether your hike will start in the sun or shade.
              </p>
            `}
          </div>

          <div style="display: flex; justify-content: flex-end; margin-top: 4px; border-top: 1px solid rgba(255,255,255,0.08); padding-top: 12px;">
            <button id="btn-ar-guide-gotit" style="display: flex; align-items: center; gap: 6px; padding: 9px 22px; background: linear-gradient(135deg, #f97316 0%, #38bdf8 100%); border: none; border-radius: 9999px; color: #ffffff; font-weight: 700; font-size: 0.85rem; cursor: pointer; box-shadow: 0 4px 15px rgba(56, 189, 248, 0.35); transition: all 0.15s ease;">
              <span>Got it • Explore 3D View 🚀</span>
            </button>
          </div>
        </div>
      </div>

      <!-- FULL-WIDTH BOTTOM CONTROL DOCK -->
      <div id="ar-bottom-dock" style="pointer-events: auto; width: 100vw; background: rgba(15, 23, 42, 0.94); backdrop-filter: blur(20px); -webkit-backdrop-filter: blur(20px); border-top: 1px solid rgba(255, 255, 255, 0.15); padding: ${this.isDockCollapsed ? '8px 24px' : '10px 24px 12px 24px'}; box-shadow: 0 -4px 30px rgba(0,0,0,0.6); display: flex; flex-direction: column; gap: 8px; transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1); box-sizing: border-box;">
        
        <!-- DOCK HEADER: Title, Collapsed Summary, Collapse Toggle & Direct Exit -->
        <div id="ar-dock-header" style="display: flex; align-items: center; justify-content: space-between; gap: 8px; ${this.isDockCollapsed ? '' : 'border-bottom: 1px solid rgba(255, 255, 255, 0.08); padding-bottom: 6px;'}">
          <div style="display: flex; align-items: center; gap: 8px; min-width: 0;">
            <span style="font-size: 0.95rem;">${this.mode === 'milky-way' ? '🌌' : '☀️🌙'}</span>
            <span style="font-size: 0.82rem; font-weight: 700; color: #f8fafc; letter-spacing: 0.3px; white-space: nowrap;">
              ${this.mode === 'milky-way' ? 'Milky Way 3D AR' : 'Sun & Moon 3D'}
            </span>
            
            <!-- Collapsed Mini Date/Time Quick Controls -->
            <div id="ar-dock-collapsed-summary" style="display: ${this.isDockCollapsed ? 'flex' : 'none'}; align-items: center; gap: 4px;">
              <button id="btn-ar-mini-prev-hour" title="Previous Hour" style="padding: 2px 7px; background: rgba(255,255,255,0.08); border: 1px solid rgba(255,255,255,0.12); border-radius: 4px; color: #cbd5e1; font-size: 0.68rem; font-weight: 700; cursor: pointer;">-1h</button>
              <span id="ar-dock-mini-datetime" style="font-size: 0.74rem; font-weight: 700; color: #38bdf8; background: rgba(56, 189, 248, 0.15); border: 1px solid rgba(56, 189, 248, 0.3); border-radius: 9999px; padding: 2px 9px; white-space: nowrap;">
                Today • 6:00 PM
              </span>
              <button id="btn-ar-mini-next-hour" title="Next Hour" style="padding: 2px 7px; background: rgba(255,255,255,0.08); border: 1px solid rgba(255,255,255,0.12); border-radius: 4px; color: #cbd5e1; font-size: 0.68rem; font-weight: 700; cursor: pointer;">+1h</button>
            </div>
          </div>

          <div style="display: flex; align-items: center; gap: 6px; flex-shrink: 0;">
            ${this.mode === 'milky-way' ? `
              <button id="btn-ar-aim-milkyway" title="Aim 3D camera directly toward Galactic Core" style="display: flex; align-items: center; gap: 4px; padding: 4px 10px; background: linear-gradient(135deg, rgba(168, 85, 247, 0.4) 0%, rgba(56, 189, 248, 0.4) 100%); border: 1px solid rgba(168, 85, 247, 0.7); border-radius: 7px; color: #f8fafc; font-size: 0.72rem; font-weight: 700; cursor: pointer; transition: all 0.15s ease; box-shadow: 0 0 10px rgba(168, 85, 247, 0.3);">
                <span>🎯 Aim at Milky Way</span>
              </button>
            ` : ''}

            <!-- Collapse / Expand Button -->
            <button id="btn-ar-dock-collapse" title="Collapse controls to view landscape without obstruction" style="display: flex; align-items: center; gap: 4px; padding: 4px 10px; background: rgba(255, 255, 255, 0.08); border: 1px solid rgba(255, 255, 255, 0.15); border-radius: 7px; color: #e2e8f0; font-size: 0.72rem; font-weight: 700; cursor: pointer; transition: all 0.15s ease;">
              <span id="ar-dock-collapse-icon">${this.isDockCollapsed ? '▴' : '▾'}</span>
              <span id="ar-dock-collapse-text">${this.isDockCollapsed ? 'Expand' : 'Collapse'}</span>
            </button>

            <!-- Direct Exit Button on Control Box (Enlarged prominent button) -->
            <button id="btn-ar-dock-exit" title="Exit 3D View and return to 2D map" style="display: flex; align-items: center; gap: 6px; padding: 7px 18px; background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%); border: 1px solid rgba(255, 255, 255, 0.3); border-radius: 8px; color: #ffffff; font-size: 0.84rem; font-weight: 800; cursor: pointer; transition: all 0.15s ease; box-shadow: 0 4px 14px rgba(239, 68, 68, 0.45); white-space: nowrap;">
              <span>◀ Exit 3D View</span>
            </button>
          </div>
        </div>

        <!-- DOCK BODY: Expandable Control Rows -->
        <div id="ar-dock-body" style="display: ${this.isDockCollapsed ? 'none' : 'flex'}; flex-direction: column; gap: 8px;">
          <!-- ROW 1: DATE NAVIGATION -->
          <div style="display: flex; align-items: center; justify-content: space-between; border-bottom: 1px solid rgba(255, 255, 255, 0.06); padding-bottom: 6px;">
            <div style="display: flex; align-items: center; gap: 6px;">
              <button id="btn-ar-prev-day" style="padding: 4px 9px; background: rgba(255, 255, 255, 0.08); border: 1px solid rgba(255, 255, 255, 0.1); border-radius: 6px; color: #cbd5e1; font-size: 0.74rem; font-weight: 600; cursor: pointer; transition: all 0.15s ease;">
                ◀ Prev Day
              </button>
              <button id="btn-ar-date-picker" style="display: flex; align-items: center; gap: 6px; padding: 4px 11px; background: rgba(56, 189, 248, 0.15); border: 1px solid rgba(56, 189, 248, 0.35); border-radius: 6px; color: #38bdf8; font-size: 0.78rem; font-weight: 700; cursor: pointer; transition: all 0.15s ease;">
                📅 <span id="ar-date-label">Today</span> ▾
              </button>
              <button id="btn-ar-next-day" style="padding: 4px 9px; background: rgba(255, 255, 255, 0.08); border: 1px solid rgba(255, 255, 255, 0.1); border-radius: 6px; color: #cbd5e1; font-size: 0.74rem; font-weight: 600; cursor: pointer; transition: all 0.15s ease;">
                Next Day ▶
              </button>
              <button id="btn-ar-today-jump" style="padding: 3px 7px; background: transparent; border: 1px solid rgba(255, 255, 255, 0.1); border-radius: 4px; color: #94a3b8; font-size: 0.68rem; font-weight: 600; cursor: pointer;">
                Jump to Today
              </button>
            </div>

            <!-- In Milky Way mode, show Moon Phase chip -->
            ${this.mode === 'milky-way' ? `
              <span id="ar-mw-moon-phase-badge" style="font-size: 0.72rem; color: #38bdf8; background: rgba(56, 189, 248, 0.12); border: 1px solid rgba(56, 189, 248, 0.25); border-radius: 9999px; padding: 2px 10px; display: inline-flex; align-items: center; gap: 5px;">
                🌙 Moon Phase Loading...
              </span>
            ` : ''}
          </div>

          <!-- ROW 2: TIME SCRUBBER -->
          <div style="display: flex; flex-direction: column; gap: 4px;">
            <div style="display: flex; justify-content: space-between; align-items: center;">
              <div style="display: flex; align-items: center; gap: 8px;">
                <span style="font-size: 0.74rem; font-weight: 700; color: #94a3b8; text-transform: uppercase; letter-spacing: 0.5px;">Time:</span>
                <span id="ar-time-label" style="font-size: 0.92rem; font-weight: 800; color: #f8fafc;">6:00 PM</span>
              </div>
              <div style="display: flex; gap: 4px;">
                <button id="btn-ar-hour-minus" style="padding: 2px 7px; background: rgba(255, 255, 255, 0.08); border: 1px solid rgba(255, 255, 255, 0.1); border-radius: 4px; color: #e2e8f0; font-size: 0.70rem; font-weight: 700; cursor: pointer;">
                  -1h
                </button>
                <button id="btn-ar-hour-plus" style="padding: 2px 7px; background: rgba(255, 255, 255, 0.08); border: 1px solid rgba(255, 255, 255, 0.1); border-radius: 4px; color: #e2e8f0; font-size: 0.70rem; font-weight: 700; cursor: pointer;">
                  +1h
                </button>
              </div>
            </div>

            <div style="display: flex; align-items: center; gap: 10px;">
              <span style="font-size: 0.65rem; color: #64748b; font-weight: 600;">00:00</span>
              <input type="range" id="ar-time-slider" min="0" max="23.99" step="0.05" value="${state.arSelectedHour || 18}" style="flex: 1; height: 6px; border-radius: 3px; accent-color: ${this.mode === 'milky-way' ? '#a855f7' : (state.arTargetMode === 'moon' ? '#38bdf8' : '#f97316')}; cursor: pointer; outline: none; background: rgba(255,255,255,0.15);">
              <span style="font-size: 0.65rem; color: #64748b; font-weight: 600;">23:59</span>
            </div>
          </div>

          <!-- ROW 2b: 24H MILKY WAY CORE ABOVE-HORIZON TIMELINE TRACK (Only in milky-way mode) -->
          ${this.mode === 'milky-way' ? `
            <div class="gw-milkyway-core-track-container" id="ar-milkyway-track-container">
              <div style="display: flex; justify-content: space-between; align-items: center; font-size: 0.70rem; color: #cbd5e1; gap: 8px;">
                <span style="font-weight: 700; color: #a855f7; display: flex; align-items: center; gap: 4px; white-space: nowrap;">
                  🌌 Galactic Core Horizon &amp; Visibility:
                </span>
                <span id="ar-mw-track-summary" style="font-size: 0.68rem; color: #cbd5e1; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;">Calculating...</span>
              </div>
              <div class="gw-milkyway-core-track-bar" id="ar-mw-track-bar" title="Click anywhere along 24h timeline to jump">
                <div id="ar-mw-cursor" class="gw-milkyway-core-cursor"></div>
              </div>
              <div style="display: flex; justify-content: space-between; font-size: 0.60rem; color: #64748b; font-weight: 600;">
                <span>12 AM</span>
                <span>3 AM</span>
                <span>6 AM</span>
                <span>9 AM</span>
                <span>12 PM</span>
                <span>3 PM</span>
                <span>6 PM</span>
                <span>9 PM</span>
                <span>12 AM</span>
              </div>
            </div>
          ` : ''}

          <!-- ROW 3: QUICK PRESET CHIPS -->
          <div id="ar-quick-presets" style="display: flex; align-items: center; gap: 5px; overflow-x: auto; padding-bottom: 2px;">
            <!-- Populated in update -->
          </div>

          <!-- ROW 4: CAMERA PERSPECTIVE MODE (FIRST PERSON vs OBSERVER) -->
          <div style="display: flex; align-items: center; justify-content: space-between; border-top: 1px solid rgba(255, 255, 255, 0.06); padding-top: 6px;">
            <span style="font-size: 0.70rem; font-weight: 700; color: #94a3b8; text-transform: uppercase; letter-spacing: 0.5px; display: flex; align-items: center; gap: 4px;">
              👁️ Perspective:
            </span>

            <div style="display: flex; gap: 4px;">
              <button class="ar-perspective-btn ${(state.arPerspectiveMode || 'first-person') === 'first-person' ? 'active' : ''}" data-perspective="first-person" style="padding: 3px 9px; border-radius: 5px; border: 1px solid rgba(255,255,255,0.12); font-size: 0.70rem; font-weight: 700; cursor: pointer; transition: all 0.15s ease;">
                👤 First Person (At POI)
              </button>
              <button class="ar-perspective-btn ${state.arPerspectiveMode === 'observer' ? 'active' : ''}" data-perspective="observer" style="padding: 3px 9px; border-radius: 5px; border: 1px solid rgba(255,255,255,0.12); font-size: 0.70rem; font-weight: 700; cursor: pointer; transition: all 0.15s ease;">
                🌐 Observer (Overview)
              </button>
            </div>
          </div>

          <!-- ROW 5: LENS FOCAL LENGTH (FOV SIMULATION) -->
          <div style="display: flex; align-items: center; justify-content: space-between; border-top: 1px solid rgba(255, 255, 255, 0.06); padding-top: 6px;">
            <span style="font-size: 0.70rem; font-weight: 700; color: #94a3b8; text-transform: uppercase; letter-spacing: 0.5px; display: flex; align-items: center; gap: 4px;">
              📷 Lens:
            </span>

            <div style="display: flex; gap: 4px;">
              <button class="ar-lens-btn ${state.arFocalLength === 24 ? 'active' : ''}" data-focal="24" style="padding: 3px 8px; border-radius: 5px; border: 1px solid rgba(255,255,255,0.12); font-size: 0.70rem; font-weight: 700; cursor: pointer; transition: all 0.15s ease;">
                24mm (Wide)
              </button>
              <button class="ar-lens-btn ${state.arFocalLength === 35 ? 'active' : ''}" data-focal="35" style="padding: 3px 8px; border-radius: 5px; border: 1px solid rgba(255,255,255,0.12); font-size: 0.70rem; font-weight: 700; cursor: pointer; transition: all 0.15s ease;">
                35mm (Std)
              </button>
              <button class="ar-lens-btn ${state.arFocalLength === 50 ? 'active' : ''}" data-focal="50" style="padding: 3px 8px; border-radius: 5px; border: 1px solid rgba(255,255,255,0.12); font-size: 0.70rem; font-weight: 700; cursor: pointer; transition: all 0.15s ease;">
                50mm (Normal)
              </button>
              <button class="ar-lens-btn ${state.arFocalLength === 100 ? 'active' : ''}" data-focal="100" style="padding: 3px 8px; border-radius: 5px; border: 1px solid rgba(255,255,255,0.12); font-size: 0.70rem; font-weight: 700; cursor: pointer; transition: all 0.15s ease;">
                100mm (Tele)
              </button>
              <button class="ar-lens-btn ${state.arFocalLength === 200 ? 'active' : ''}" data-focal="200" style="padding: 3px 8px; border-radius: 5px; border: 1px solid rgba(255,255,255,0.12); font-size: 0.70rem; font-weight: 700; cursor: pointer; transition: all 0.15s ease;">
                200mm (Super)
              </button>
            </div>
          </div>
        </div>

      </div>
    `;

    this.container.appendChild(root);
    this.rootEl = root;
    this.calendarPopupEl = root.querySelector('#ar-calendar-popup');

    // If first time opening AR view, show guide modal automatically
    const guideKey = this.mode === 'milky-way' ? 'gw_milky_way_ar_guide_seen' : 'gw_sun_moon_ar_guide_seen';
    const hasSeenGuide = localStorage.getItem(guideKey);
    if (!hasSeenGuide) {
      const guideModal = root.querySelector('#ar-guide-modal') as HTMLElement;
      if (guideModal) {
        guideModal.style.display = 'flex';
      }
    }

    this.bindEvents();
    this.update(state);
  }

  private bindEvents() {
    if (!this.rootEl) return;

    // Exit Button
    const exitBtn = this.rootEl.querySelector('#btn-ar-exit');
    if (exitBtn) {
      exitBtn.addEventListener('click', () => {
        if (this.callbacks) this.callbacks.onExit();
      });
    }

    // Direct Exit Button on Dock
    const dockExitBtn = this.rootEl.querySelector('#btn-ar-dock-exit');
    if (dockExitBtn) {
      dockExitBtn.addEventListener('click', () => {
        if (this.callbacks) this.callbacks.onExit();
      });
    }

    // Aim at Milky Way Button
    const aimMilkyWayBtn = this.rootEl.querySelector('#btn-ar-aim-milkyway');
    if (aimMilkyWayBtn) {
      aimMilkyWayBtn.addEventListener('click', () => {
        if (this.callbacks && this.callbacks.onAimMilkyWay) {
          this.callbacks.onAimMilkyWay();
        }
      });
    }

    // Collapse / Expand Button on Dock
    const dockCollapseBtn = this.rootEl.querySelector('#btn-ar-dock-collapse');
    if (dockCollapseBtn) {
      dockCollapseBtn.addEventListener('click', () => {
        this.isDockCollapsed = !this.isDockCollapsed;
        this.updateDockCollapseState();
      });
    }

    // Mini -1h / +1h Buttons in Collapsed Dock Summary
    const miniPrevHourBtn = this.rootEl.querySelector('#btn-ar-mini-prev-hour');
    if (miniPrevHourBtn) {
      miniPrevHourBtn.addEventListener('click', () => {
        const current = store.getState().arSelectedHour || 18;
        const prev = Math.max(0, current - 1.0);
        if (this.callbacks) this.callbacks.onTimeChange(prev);
      });
    }

    const miniNextHourBtn = this.rootEl.querySelector('#btn-ar-mini-next-hour');
    if (miniNextHourBtn) {
      miniNextHourBtn.addEventListener('click', () => {
        const current = store.getState().arSelectedHour || 18;
        const next = Math.min(23.99, current + 1.0);
        if (this.callbacks) this.callbacks.onTimeChange(next);
      });
    }

    // Help Button
    const helpBtn = this.rootEl.querySelector('#btn-ar-help');
    const guideModal = this.rootEl.querySelector('#ar-guide-modal') as HTMLElement;
    if (helpBtn && guideModal) {
      helpBtn.addEventListener('click', () => {
        guideModal.style.display = 'flex';
      });
    }

    // Guide Modal Got It / Close Buttons
    const guideGotItBtn = this.rootEl.querySelector('#btn-ar-guide-gotit');
    const guideCloseBtn = this.rootEl.querySelector('#btn-ar-guide-close');
    const dismissGuide = () => {
      if (guideModal) guideModal.style.display = 'none';
      try {
        const guideKey = this.mode === 'milky-way' ? 'gw_milky_way_ar_guide_seen' : 'gw_sun_moon_ar_guide_seen';
        localStorage.setItem(guideKey, 'true');
      } catch (e) {
        // ignore localStorage restriction
      }
      if (this.callbacks) {
        this.callbacks.onZoomToMarker();
      }
    };
    if (guideGotItBtn) guideGotItBtn.addEventListener('click', dismissGuide);
    if (guideCloseBtn) guideCloseBtn.addEventListener('click', dismissGuide);

    // Aim towards Milky Way button
    const aimMwBtn = this.rootEl.querySelector('#btn-ar-aim-mw');
    if (aimMwBtn) {
      aimMwBtn.addEventListener('click', () => {
        if (this.callbacks && this.callbacks.onAimMilkyWay) {
          this.callbacks.onAimMilkyWay();
        }
      });
    }

    // Recenter/Aim target in Sun/Moon mode
    const recenterBtn = this.rootEl.querySelector('#btn-ar-recenter');
    if (recenterBtn) {
      recenterBtn.addEventListener('click', () => {
        if (this.callbacks) this.callbacks.onCenterTarget();
      });
    }

    // Zoom to Marker
    const zoomMarkerBtn = this.rootEl.querySelector('#btn-ar-zoom-marker');
    if (zoomMarkerBtn) {
      zoomMarkerBtn.addEventListener('click', () => {
        if (this.callbacks) this.callbacks.onZoomToMarker();
      });
    }

    // Target Mode: Sun
    const sunModeBtn = this.rootEl.querySelector('#btn-ar-mode-sun');
    if (sunModeBtn) {
      sunModeBtn.addEventListener('click', () => {
        if (this.callbacks && this.callbacks.onTargetModeChange) {
          this.callbacks.onTargetModeChange('sun');
        }
      });
    }

    // Target Mode: Moon
    const moonModeBtn = this.rootEl.querySelector('#btn-ar-mode-moon');
    if (moonModeBtn) {
      moonModeBtn.addEventListener('click', () => {
        if (this.callbacks && this.callbacks.onTargetModeChange) {
          this.callbacks.onTargetModeChange('moon');
        }
      });
    }

    // Time Slider
    const timeSlider = this.rootEl.querySelector('#ar-time-slider') as HTMLInputElement;
    if (timeSlider) {
      timeSlider.addEventListener('input', () => {
        const val = parseFloat(timeSlider.value);
        if (this.callbacks) this.callbacks.onTimeChange(val);
      });
    }

    // Milky Way 24H Timeline Track click to jump
    const mwTrackBar = this.rootEl.querySelector('#ar-mw-track-bar');
    if (mwTrackBar) {
      mwTrackBar.addEventListener('click', (e: any) => {
        const rect = (mwTrackBar as HTMLElement).getBoundingClientRect();
        const pct = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
        const targetHour = pct * 23.99;
        if (this.callbacks) this.callbacks.onTimeChange(targetHour);
      });
    }

    // Hour Plus / Minus
    const hourPlusBtn = this.rootEl.querySelector('#btn-ar-hour-plus');
    if (hourPlusBtn) {
      hourPlusBtn.addEventListener('click', () => {
        const current = store.getState().arSelectedHour || 18;
        const next = Math.min(23.99, current + 1.0);
        if (this.callbacks) this.callbacks.onTimeChange(next);
      });
    }

    const hourMinusBtn = this.rootEl.querySelector('#btn-ar-hour-minus');
    if (hourMinusBtn) {
      hourMinusBtn.addEventListener('click', () => {
        const current = store.getState().arSelectedHour || 18;
        const prev = Math.max(0, current - 1.0);
        if (this.callbacks) this.callbacks.onTimeChange(prev);
      });
    }

    // Prev Day
    const prevDayBtn = this.rootEl.querySelector('#btn-ar-prev-day');
    if (prevDayBtn) {
      prevDayBtn.addEventListener('click', () => {
        const state = store.getState();
        const current = state.arSelectedDate ? new Date(state.arSelectedDate) : new Date();
        const prev = new Date(current.getTime() - 24 * 3600 * 1000);
        if (this.callbacks) this.callbacks.onDateChange(prev);
      });
    }

    // Next Day
    const nextDayBtn = this.rootEl.querySelector('#btn-ar-next-day');
    if (nextDayBtn) {
      nextDayBtn.addEventListener('click', () => {
        const state = store.getState();
        const current = state.arSelectedDate ? new Date(state.arSelectedDate) : new Date();
        const next = new Date(current.getTime() + 24 * 3600 * 1000);
        if (this.callbacks) this.callbacks.onDateChange(next);
      });
    }

    // Jump to Today
    const todayJumpBtn = this.rootEl.querySelector('#btn-ar-today-jump');
    if (todayJumpBtn) {
      todayJumpBtn.addEventListener('click', () => {
        if (this.callbacks) this.callbacks.onDateChange(new Date());
      });
    }

    // Date Picker toggle
    const datePickerBtn = this.rootEl.querySelector('#btn-ar-date-picker');
    if (datePickerBtn) {
      datePickerBtn.addEventListener('click', () => {
        this.isCalendarOpen = !this.isCalendarOpen;
        if (this.calendarPopupEl) {
          this.calendarPopupEl.style.display = this.isCalendarOpen ? 'block' : 'none';
          if (this.isCalendarOpen) {
            this.renderCalendarContent(store.getState());
          }
        }
      });
    }

    // Perspective buttons
    const perspectiveBtns = this.rootEl.querySelectorAll('.ar-perspective-btn');
    perspectiveBtns.forEach(btn => {
      btn.addEventListener('click', () => {
        const mode = (btn as HTMLElement).dataset.perspective as 'first-person' | 'observer';
        if (this.callbacks && this.callbacks.onPerspectiveModeChange && mode) {
          this.callbacks.onPerspectiveModeChange(mode);
        }
      });
    });

    // Focal length buttons
    const lensBtns = this.rootEl.querySelectorAll('.ar-lens-btn');
    lensBtns.forEach(btn => {
      btn.addEventListener('click', () => {
        const mm = parseInt((btn as HTMLElement).dataset.focal || '35', 10);
        if (this.callbacks && this.callbacks.onFocalLengthChange) {
          this.callbacks.onFocalLengthChange(mm);
        }
      });
    });
  }

  private updateTelemetry(state: AppState) {
    if (!this.rootEl) return;
    const iconEl = this.rootEl.querySelector('#ar-telemetry-icon');
    const textEl = this.rootEl.querySelector('#ar-telemetry-text');
    const badgeEl = this.rootEl.querySelector('#ar-telemetry-badge') as HTMLElement;
    if (!iconEl || !textEl || !badgeEl) return;

    const poi = state.selectedPoi;
    const lat = poi ? poi.lat : state.mapCenter[1];
    const lon = poi ? poi.lng : state.mapCenter[0];
    const tz = state.userTimezone || 'America/Los_Angeles';

    const selectedDate = state.arSelectedDate || new Date();
    const hourDecimal = state.arSelectedHour !== undefined ? state.arSelectedHour : 18;
    const hourInt = Math.floor(hourDecimal);
    const minInt = Math.floor((hourDecimal - hourInt) * 60);

    const dFmt = new Intl.DateTimeFormat('en-US', { timeZone: tz, year: 'numeric', month: 'numeric', day: 'numeric' });
    const parts = dFmt.formatToParts(selectedDate);
    const getPart = (type: string) => parseInt(parts.find(p => p.type === type)?.value || '0', 10);
    const y = getPart('year');
    const m = getPart('month') - 1;
    const d = getPart('day');
    const dt = getUtcDateForLocalTime(y, m, d, hourInt, minInt, tz);
    const effectiveDt = new Date(dt.getTime());

    const getCompassDir = (deg: number): string => {
      const dirs = ['N', 'NNE', 'NE', 'ENE', 'E', 'ESE', 'SE', 'SSE', 'S', 'SSW', 'SW', 'WSW', 'W', 'WNW', 'NW', 'NNW'];
      return dirs[Math.round(deg / 22.5) % 16];
    };

    if (this.mode === 'milky-way') {
      const core = getGalacticCorePosition(lat, lon, effectiveDt);
      const azDeg = Math.round(((core.azimuth % 360) + 360) % 360);
      const altDeg = core.altitude.toFixed(1);
      const altNum = parseFloat(altDeg);

      iconEl.textContent = '🌌';
      textEl.textContent = `CORE: ${azDeg}° ${getCompassDir(azDeg)} • Alt ${altNum > 0 ? `+${altDeg}` : altDeg}° (${core.visibilityReason})`;
      if (core.isVisible) {
        badgeEl.style.borderColor = 'rgba(168, 85, 247, 0.7)';
        badgeEl.style.boxShadow = '0 0 15px rgba(168, 85, 247, 0.35)';
      } else {
        badgeEl.style.borderColor = 'rgba(255, 255, 255, 0.2)';
        badgeEl.style.boxShadow = 'none';
      }
    } else if (state.arTargetMode === 'sun') {
      const sunPos = SunCalc.getPosition(effectiveDt, lat, lon);
      const azDeg = Math.round(((sunPos.azimuth % 360) + 360) % 360);
      const altDeg = sunPos.altitude.toFixed(1);
      const altNum = parseFloat(altDeg);

      let phaseLabel = 'Daylight';
      if (altNum < -18) phaseLabel = 'Night';
      else if (altNum < -12) phaseLabel = 'Astro Twilight';
      else if (altNum < -6) phaseLabel = 'Nautical Twilight';
      else if (altNum < -0.83) phaseLabel = 'Blue Hour / Civil Dusk';
      else if (altNum <= 6.5) phaseLabel = 'Golden Hour ✨';

      iconEl.textContent = '☀️';
      textEl.textContent = `SUN: ${azDeg}° ${getCompassDir(azDeg)} • Alt ${altNum > 0 ? `+${altDeg}` : altDeg}° (${phaseLabel})`;
      badgeEl.style.borderColor = 'rgba(249, 115, 22, 0.5)';
      badgeEl.style.boxShadow = 'none';
    } else {
      const moonPos = SunCalc.getMoonPosition(effectiveDt, lat, lon);
      const moonIllum = SunCalc.getMoonIllumination(effectiveDt);
      const azDeg = Math.round(((moonPos.azimuth % 360) + 360) % 360);
      const altDeg = moonPos.altitude.toFixed(1);
      const altNum = parseFloat(altDeg);
      const illumPct = Math.round(moonIllum.fraction * 100);
      const phaseDetails = getMoonPhaseDetails(effectiveDt);

      iconEl.textContent = phaseDetails.icon || '🌙';
      textEl.textContent = `MOON: ${azDeg}° ${getCompassDir(azDeg)} • Alt ${altNum > 0 ? `+${altDeg}` : altDeg}° • ${phaseDetails.name} (${illumPct}%)`;
      badgeEl.style.borderColor = 'rgba(56, 189, 248, 0.5)';
      badgeEl.style.boxShadow = 'none';
    }
  }

  private updateMilkyWayTimeline(state: AppState) {
    if (!this.rootEl || this.mode !== 'milky-way') return;
    const trackBar = this.rootEl.querySelector('#ar-mw-track-bar') as HTMLElement | null;
    const summaryEl = this.rootEl.querySelector('#ar-mw-track-summary');
    const cursorEl = this.rootEl.querySelector('#ar-mw-cursor') as HTMLElement | null;
    if (!trackBar || !summaryEl || !cursorEl) return;

    const poi = state.selectedPoi;
    const lat = poi ? poi.lat : state.mapCenter[1];
    const lon = poi ? poi.lng : state.mapCenter[0];
    const tz = state.userTimezone || 'America/Los_Angeles';
    const date = state.arSelectedDate || new Date();
    const hourDecimal = state.arSelectedHour !== undefined ? state.arSelectedHour : 18;

    const timeline = getGalacticCoreDayTimeline(lat, lon, date, tz);

    summaryEl.innerHTML = timeline.hasVisibility
      ? `Rise: <strong style="color:#f8fafc;">${timeline.riseLabel}</strong> • Peak: <strong style="color:#f8fafc;">${timeline.peakLabel}</strong> • Set: <strong style="color:#f8fafc;">${timeline.setLabel}</strong> • <span style="color:#38bdf8;">${timeline.primeWindowLabel}</span>`
      : `<span style="color:#94a3b8;">Milky Way core does not rise above horizon today</span>`;

    // Construct continuous gradient ribbon across the 24 hours (0% to 100%)
    if (timeline.samples && timeline.samples.length > 0) {
      const gradientStops = timeline.samples.map(sample => {
        const pct = (sample.hourDec / 24) * 100;
        let color = '#1e293b'; // daylight / twilight
        if (sample.isPrimeViewing) {
          color = '#8b5cf6'; // prime dark viewing
        } else if (sample.isCoreAboveHorizon) {
          color = '#4338ca'; // core above horizon but some twilight
        } else if (sample.isDarkNight) {
          color = '#090d16'; // dark sky night without core
        }
        return `${color} ${pct.toFixed(1)}%`;
      }).join(', ');

      trackBar.style.background = `linear-gradient(90deg, ${gradientStops})`;
    }

    // Position cursor
    const cursorPct = Math.max(0, Math.min(100, (hourDecimal / 24) * 100));
    cursorEl.style.left = `${cursorPct}%`;
  }

  private updateMilkyWayMoonDisplay(state: AppState) {
    if (!this.rootEl || this.mode !== 'milky-way') return;
    const badgeEl = this.rootEl.querySelector('#ar-mw-moon-phase-badge');
    if (!badgeEl) return;

    const poi = state.selectedPoi;
    const lat = poi ? poi.lat : state.mapCenter[1];
    const lon = poi ? poi.lng : state.mapCenter[0];
    const date = state.arSelectedDate || new Date();
    const effectiveDt = new Date(date.getTime());

    const moonDetails = getMoonPhaseDetails(effectiveDt);
    const moonless = isMoonlessNight(effectiveDt, lat, lon);

    badgeEl.innerHTML = `${moonDetails.icon} ${moonDetails.name} (${moonDetails.illumination}) ${moonless ? '• ✨ Moonless Night' : ''}`;
  }

  private updateDateDisplay(state: AppState) {
    if (!this.rootEl) return;
    const labelEl = this.rootEl.querySelector('#ar-date-label');
    if (!labelEl) return;

    const tz = state.userTimezone || 'America/Los_Angeles';
    const date = state.arSelectedDate || new Date();

    const str = date.toLocaleDateString('en-US', {
      timeZone: tz,
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });

    labelEl.textContent = str;
    this.updateMiniDateTime(state);
  }

  private updateTimeDisplay(state: AppState) {
    if (!this.rootEl) return;
    const timeLabelEl = this.rootEl.querySelector('#ar-time-label');
    const sliderEl = this.rootEl.querySelector('#ar-time-slider') as HTMLInputElement;
    if (!timeLabelEl || !sliderEl) return;

    const hourDecimal = state.arSelectedHour !== undefined ? state.arSelectedHour : 18;
    if (Math.abs(parseFloat(sliderEl.value) - hourDecimal) > 0.05) {
      sliderEl.value = String(hourDecimal);
    }

    const hourInt = Math.floor(hourDecimal);
    const minInt = Math.floor((hourDecimal - hourInt) * 60);

    const period = hourInt >= 12 ? 'PM' : 'AM';
    const dispHour = hourInt % 12 === 0 ? 12 : hourInt % 12;
    const dispMin = String(minInt).padStart(2, '0');

    timeLabelEl.textContent = `${dispHour}:${dispMin} ${period}`;
    this.updateMiniDateTime(state);

    // Update Quick Preset Chips for this date
    this.renderQuickPresets(state);
  }

  private updateDockCollapseState() {
    if (!this.rootEl) return;
    const bottomDock = this.rootEl.querySelector('#ar-bottom-dock') as HTMLElement;
    const dockHeader = this.rootEl.querySelector('#ar-dock-header') as HTMLElement;
    const dockBody = this.rootEl.querySelector('#ar-dock-body') as HTMLElement;
    const collapseIcon = this.rootEl.querySelector('#ar-dock-collapse-icon');
    const collapseText = this.rootEl.querySelector('#ar-dock-collapse-text');
    const collapsedSummary = this.rootEl.querySelector('#ar-dock-collapsed-summary') as HTMLElement;

    if (bottomDock) {
      bottomDock.style.padding = this.isDockCollapsed ? '8px 14px' : '12px 18px';
    }
    if (dockHeader) {
      dockHeader.style.borderBottom = this.isDockCollapsed ? 'none' : '1px solid rgba(255, 255, 255, 0.08)';
      dockHeader.style.paddingBottom = this.isDockCollapsed ? '0px' : '8px';
    }
    if (dockBody) {
      dockBody.style.display = this.isDockCollapsed ? 'none' : 'flex';
    }
    if (collapseIcon) {
      collapseIcon.textContent = this.isDockCollapsed ? '▴' : '▾';
    }
    if (collapseText) {
      collapseText.textContent = this.isDockCollapsed ? 'Expand Controls' : 'Collapse';
    }
    if (collapsedSummary) {
      collapsedSummary.style.display = this.isDockCollapsed ? 'flex' : 'none';
    }
    this.updateMiniDateTime(store.getState());
  }

  private updateMiniDateTime(state: AppState) {
    if (!this.rootEl) return;
    const miniDateTimeEl = this.rootEl.querySelector('#ar-dock-mini-datetime');
    if (!miniDateTimeEl) return;

    const tz = state.userTimezone || 'America/Los_Angeles';
    const date = state.arSelectedDate || new Date();
    const hourDecimal = state.arSelectedHour !== undefined ? state.arSelectedHour : 18;
    const hourInt = Math.floor(hourDecimal);
    const minInt = Math.floor((hourDecimal - hourInt) * 60);
    const period = hourInt >= 12 ? 'PM' : 'AM';
    const dispHour = hourInt % 12 === 0 ? 12 : hourInt % 12;
    const dispMin = String(minInt).padStart(2, '0');

    const now = new Date();
    const dFmt = new Intl.DateTimeFormat('en-US', { timeZone: tz, month: 'short', day: 'numeric', year: 'numeric' });
    const isToday = dFmt.format(date) === dFmt.format(now);
    const dayLabel = isToday ? 'Today' : date.toLocaleDateString('en-US', { timeZone: tz, weekday: 'short', month: 'short', day: 'numeric' });

    miniDateTimeEl.textContent = `${dayLabel} • ${dispHour}:${dispMin} ${period}`;
  }

  private renderQuickPresets(state: AppState) {
    if (!this.rootEl) return;
    const container = this.rootEl.querySelector('#ar-quick-presets');
    if (!container) return;

    const poi = state.selectedPoi;
    const lat = poi ? poi.lat : state.mapCenter[1];
    const lon = poi ? poi.lng : state.mapCenter[0];
    const tz = state.userTimezone || 'America/Los_Angeles';
    const date = state.arSelectedDate || new Date();

    const sTimes = SunCalc.getTimes(date, lat, lon);
    const mTimes = SunCalc.getMoonTimes(date, lat, lon);

    const getTimeDecimal = (d?: Date | null): number => {
      if (!d || isNaN(d.getTime())) return -1;
      const parts = new Intl.DateTimeFormat('en-US', {
        timeZone: tz,
        hour: 'numeric',
        minute: 'numeric',
        hour12: false
      }).formatToParts(d);
      let h = parseInt(parts.find(p => p.type === 'hour')?.value || '0', 10);
      if (h === 24) h = 0;
      const m = parseInt(parts.find(p => p.type === 'minute')?.value || '0', 10);
      return h + m / 60;
    };

    const fmtChipTime = (d?: Date | null): string => {
      if (!d || isNaN(d.getTime())) return '--';
      return d.toLocaleTimeString('en-US', { timeZone: tz, hour: 'numeric', minute: '2-digit' });
    };

    const chips: Array<{ label: string; icon: string; decimal: number; timeStr: string; color: string; action?: string }> = [];

    if (this.mode === 'milky-way') {
      chips.push({ label: 'Aim Core', icon: '🎯', decimal: -999, timeStr: 'Now', color: '#c084fc', action: 'aim' });
      const timeline = getGalacticCoreDayTimeline(lat, lon, date, tz);
      if (timeline.hasVisibility) {
        if (timeline.riseTimeDec !== null) {
          chips.push({ label: 'Core Rise', icon: '🌌', decimal: timeline.riseTimeDec, timeStr: timeline.riseLabel, color: '#a855f7' });
        }
        if (timeline.peakTimeDec !== null) {
          chips.push({ label: `Core Peak (${Math.round(timeline.peakAltitude)}°)`, icon: '✨', decimal: timeline.peakTimeDec, timeStr: formatHourDecimal(timeline.peakTimeDec), color: '#38bdf8' });
        }
        if (timeline.setTimeDec !== null) {
          chips.push({ label: 'Core Set', icon: '🌠', decimal: timeline.setTimeDec, timeStr: timeline.setLabel, color: '#818cf8' });
        }
      }
      chips.push({ label: 'Midnight', icon: '🌙', decimal: 0.0, timeStr: '12:00 AM', color: '#94a3b8' });
    } else if (state.arTargetMode === 'sun') {
      const dawnDec = getTimeDecimal(sTimes.dawn);
      const sunriseDec = getTimeDecimal(sTimes.sunrise);
      const goldenAmTime = (sTimes.sunrise && sTimes.goldenHourEnd)
        ? new Date((sTimes.sunrise.getTime() + sTimes.goldenHourEnd.getTime()) / 2)
        : sTimes.goldenHourEnd;
      const goldenAmDec = getTimeDecimal(goldenAmTime);

      const noonDec = getTimeDecimal(sTimes.solarNoon);

      const goldenPmTime = (sTimes.goldenHour && sTimes.sunset)
        ? new Date((sTimes.goldenHour.getTime() + sTimes.sunset.getTime()) / 2)
        : sTimes.goldenHour;
      const goldenPmDec = getTimeDecimal(goldenPmTime);

      const sunsetDec = getTimeDecimal(sTimes.sunset);
      const duskDec = getTimeDecimal(sTimes.dusk);

      if (dawnDec >= 0) chips.push({ label: 'Dawn', icon: '🌄', decimal: dawnDec, timeStr: fmtChipTime(sTimes.dawn), color: '#818cf8' });
      if (sunriseDec >= 0) chips.push({ label: 'Sunrise', icon: '🌅', decimal: sunriseDec, timeStr: fmtChipTime(sTimes.sunrise), color: '#f97316' });
      if (goldenAmDec >= 0) chips.push({ label: 'AM Golden', icon: '✨', decimal: goldenAmDec, timeStr: fmtChipTime(goldenAmTime), color: '#f59e0b' });
      if (noonDec >= 0) chips.push({ label: 'Noon', icon: '☀️', decimal: noonDec, timeStr: fmtChipTime(sTimes.solarNoon), color: '#facc15' });
      if (goldenPmDec >= 0) chips.push({ label: 'PM Golden', icon: '✨', decimal: goldenPmDec, timeStr: fmtChipTime(goldenPmTime), color: '#fbbf24' });
      if (sunsetDec >= 0) chips.push({ label: 'Sunset', icon: '🌇', decimal: sunsetDec, timeStr: fmtChipTime(sTimes.sunset), color: '#f97316' });
      if (duskDec >= 0) chips.push({ label: 'Blue Hr / Dusk', icon: '🌆', decimal: duskDec, timeStr: fmtChipTime(sTimes.dusk), color: '#38bdf8' });
    } else {
      const moonriseDec = getTimeDecimal(mTimes.rise);
      const moonsetDec = getTimeDecimal(mTimes.set);

      if (moonriseDec >= 0) chips.push({ label: 'Moonrise', icon: '🌙', decimal: moonriseDec, timeStr: fmtChipTime(mTimes.rise), color: '#38bdf8' });
      if (moonsetDec >= 0) chips.push({ label: 'Moonset', icon: '🌘', decimal: moonsetDec, timeStr: fmtChipTime(mTimes.set), color: '#818cf8' });
      chips.push({ label: 'Midnight', icon: '🌌', decimal: 0.0, timeStr: '12:00 AM', color: '#a78bfa' });
    }

    const currentHour = state.arSelectedHour !== undefined ? state.arSelectedHour : 18;
    container.innerHTML = chips.map(c => {
      const isActive = c.action !== 'aim' && Math.abs(c.decimal - currentHour) < 0.15;
      const bg = isActive ? `${c.color}2b` : 'rgba(255, 255, 255, 0.06)';
      const border = isActive ? `${c.color}` : `${c.color}40`;
      const shadow = isActive ? `box-shadow: 0 0 8px ${c.color}40;` : '';
      return `
        <button class="ar-preset-chip" data-time="${c.decimal}" data-action="${c.action || ''}" style="white-space: nowrap; padding: 3px 8px; background: ${bg}; border: 1px solid ${border}; border-radius: 9999px; color: ${c.color}; font-size: 0.68rem; font-weight: 700; cursor: pointer; transition: all 0.15s ease; ${shadow}">
          ${c.icon} ${c.label} (${c.timeStr})
        </button>
      `;
    }).join('');

    container.querySelectorAll('.ar-preset-chip').forEach(btn => {
      btn.addEventListener('click', () => {
        const action = (btn as HTMLElement).dataset.action;
        if (action === 'aim') {
          if (this.callbacks && this.callbacks.onAimMilkyWay) {
            this.callbacks.onAimMilkyWay();
          }
          return;
        }
        const timeVal = parseFloat((btn as HTMLElement).dataset.time || '0');
        if (this.callbacks) this.callbacks.onTimeChange(timeVal);
      });
    });
  }

  private updateLensSelection(state: AppState) {
    if (!this.rootEl) return;
    const current = state.arFocalLength || 35;
    const btns = this.rootEl.querySelectorAll('.ar-lens-btn');
    btns.forEach(btn => {
      const mm = parseInt((btn as HTMLElement).dataset.focal || '35', 10);
      if (mm === current) {
        (btn as HTMLElement).style.background = '#38bdf8';
        (btn as HTMLElement).style.color = '#0f172a';
        (btn as HTMLElement).style.borderColor = '#38bdf8';
        (btn as HTMLElement).style.boxShadow = '0 2px 8px rgba(56, 189, 248, 0.4)';
      } else {
        (btn as HTMLElement).style.background = 'transparent';
        (btn as HTMLElement).style.color = '#94a3b8';
        (btn as HTMLElement).style.borderColor = 'rgba(255, 255, 255, 0.12)';
        (btn as HTMLElement).style.boxShadow = 'none';
      }
    });
  }

  private updateTargetModeToggle(state: AppState) {
    if (!this.rootEl) return;
    const sunBtn = this.rootEl.querySelector('#btn-ar-mode-sun') as HTMLElement;
    const moonBtn = this.rootEl.querySelector('#btn-ar-mode-moon') as HTMLElement;
    const slider = this.rootEl.querySelector('#ar-time-slider') as HTMLInputElement;

    if (state.arTargetMode === 'sun') {
      if (sunBtn) {
        sunBtn.style.background = '#f97316';
        sunBtn.style.color = '#fff';
        sunBtn.style.boxShadow = '0 2px 8px rgba(249, 115, 22, 0.4)';
      }
      if (moonBtn) {
        moonBtn.style.background = 'transparent';
        moonBtn.style.color = '#94a3b8';
        moonBtn.style.boxShadow = 'none';
      }
      if (slider) slider.style.accentColor = '#f97316';
    } else {
      if (moonBtn) {
        moonBtn.style.background = '#38bdf8';
        moonBtn.style.color = '#0f172a';
        moonBtn.style.boxShadow = '0 2px 8px rgba(56, 189, 248, 0.4)';
      }
      if (sunBtn) {
        sunBtn.style.background = 'transparent';
        sunBtn.style.color = '#94a3b8';
        sunBtn.style.boxShadow = 'none';
      }
      if (slider) slider.style.accentColor = '#38bdf8';
    }
  }

  private updatePerspectiveSelection(state: AppState) {
    if (!this.rootEl) return;
    const currentMode = state.arPerspectiveMode || 'first-person';
    const btns = this.rootEl.querySelectorAll('.ar-perspective-btn');
    btns.forEach(btn => {
      const mode = (btn as HTMLElement).dataset.perspective;
      if (mode === currentMode) {
        (btn as HTMLElement).style.background = '#38bdf8';
        (btn as HTMLElement).style.color = '#0f172a';
        (btn as HTMLElement).style.borderColor = '#38bdf8';
        (btn as HTMLElement).style.boxShadow = '0 2px 8px rgba(56, 189, 248, 0.4)';
      } else {
        (btn as HTMLElement).style.background = 'transparent';
        (btn as HTMLElement).style.color = '#94a3b8';
        (btn as HTMLElement).style.borderColor = 'rgba(255, 255, 255, 0.12)';
        (btn as HTMLElement).style.boxShadow = 'none';
      }
    });
  }

  private renderCalendarContent(state: AppState) {
    if (!this.calendarPopupEl) return;

    const poi = state.selectedPoi;
    const lat = poi ? poi.lat : state.mapCenter[1];
    const lon = poi ? poi.lng : state.mapCenter[0];
    const tz = state.userTimezone || 'America/Los_Angeles';

    const now = new Date();
    const baseYear = now.getFullYear();
    const baseMonth = now.getMonth();

    const totalMonths = baseMonth + this.calendarMonthOffset;
    const currentYear = baseYear + Math.floor(totalMonths / 12);
    const currentMonth = ((totalMonths % 12) + 12) % 12;

    const dateObj = new Date(currentYear, currentMonth, 1);
    const monthName = dateObj.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

    const selectedDate = state.arSelectedDate || new Date();
    let selectedDateStr = '';
    try {
      const dFmt = new Intl.DateTimeFormat('en-CA', { timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit' });
      selectedDateStr = dFmt.format(selectedDate);
    } catch (e) {
      selectedDateStr = selectedDate.toISOString().split('T')[0];
    }

    const days = SolarLunarService.getMonthCalendar(
      currentYear,
      currentMonth,
      lat,
      lon,
      3,
      selectedDateStr,
      tz
    );

    const firstDayIndex = new Date(currentYear, currentMonth, 1).getDay();
    const monthHeaderDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

    let gridHtml = `
      <div style="display: grid; grid-template-columns: repeat(7, 1fr); gap: 2px; font-size: 0.65rem; text-align: center; font-weight: 700; color: #94a3b8; margin-bottom: 4px;">
        ${monthHeaderDays.map(d => `<div>${d}</div>`).join('')}
      </div>
      <div style="display: grid; grid-template-columns: repeat(7, 1fr); gap: 3px;">
    `;

    for (let i = 0; i < firstDayIndex; i++) {
      gridHtml += `<div style="height: 32px; background: rgba(255,255,255,0.02); border-radius: 4px;"></div>`;
    }

    days.forEach(day => {
      let bg = 'rgba(15, 23, 42, 0.6)';
      let border = '1px solid rgba(255,255,255,0.08)';

      if (day.isSelected) {
        bg = 'rgba(56, 189, 248, 0.3)';
        border = '1.5px solid #38bdf8';
      } else if (day.isToday) {
        border = '1.5px solid #f59e0b';
      }

      gridHtml += `
        <div class="ar-cal-cell" data-datestr="${day.dateStr}" style="height: 32px; background: ${bg}; border: ${border}; border-radius: 4px; padding: 2px; cursor: pointer; display: flex; flex-direction: column; justify-content: space-between; align-items: center; transition: all 0.15s ease;">
          <span style="font-size: 0.65rem; font-weight: 700; color: ${day.isSelected ? '#38bdf8' : '#e2e8f0'};">${day.dayNumber}</span>
          <span style="font-size: 0.68rem; line-height: 1;">${day.moonIcon}</span>
        </div>
      `;
    });

    gridHtml += `</div>`;

    this.calendarPopupEl.innerHTML = `
      <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 8px;">
        <span style="font-size: 0.8rem; font-weight: 700; color: #f8fafc;">365-Day Ephemeris</span>
        <button id="btn-ar-cal-close" style="background: transparent; border: none; color: #94a3b8; font-size: 1rem; cursor: pointer; padding: 0 4px;">&times;</button>
      </div>

      <div style="display: flex; justify-content: space-between; align-items: center; background: rgba(0,0,0,0.3); padding: 4px 8px; border-radius: 6px; margin-bottom: 8px;">
        <button id="btn-ar-cal-prev-mo" ${this.calendarMonthOffset <= 0 ? 'disabled' : ''} style="background: transparent; border: none; color: ${this.calendarMonthOffset > 0 ? '#38bdf8' : '#475569'}; font-size: 0.72rem; font-weight: 700; cursor: ${this.calendarMonthOffset > 0 ? 'pointer' : 'default'};">◀ Prev</button>
        <span style="font-size: 0.75rem; font-weight: 700; color: #f8fafc;">${monthName}</span>
        <button id="btn-ar-cal-next-mo" ${this.calendarMonthOffset >= 11 ? 'disabled' : ''} style="background: transparent; border: none; color: ${this.calendarMonthOffset < 11 ? '#38bdf8' : '#475569'}; font-size: 0.72rem; font-weight: 700; cursor: ${this.calendarMonthOffset < 11 ? 'pointer' : 'default'};">Next ▶</button>
      </div>

      ${gridHtml}
    `;

    // Calendar listeners
    const closeBtn = this.calendarPopupEl.querySelector('#btn-ar-cal-close');
    if (closeBtn) {
      closeBtn.addEventListener('click', () => {
        this.isCalendarOpen = false;
        if (this.calendarPopupEl) this.calendarPopupEl.style.display = 'none';
      });
    }

    const prevMoBtn = this.calendarPopupEl.querySelector('#btn-ar-cal-prev-mo');
    if (prevMoBtn) {
      prevMoBtn.addEventListener('click', () => {
        if (this.calendarMonthOffset > 0) {
          this.calendarMonthOffset--;
          this.renderCalendarContent(store.getState());
        }
      });
    }

    const nextMoBtn = this.calendarPopupEl.querySelector('#btn-ar-cal-next-mo');
    if (nextMoBtn) {
      nextMoBtn.addEventListener('click', () => {
        if (this.calendarMonthOffset < 11) {
          this.calendarMonthOffset++;
          this.renderCalendarContent(store.getState());
        }
      });
    }

    this.calendarPopupEl.querySelectorAll('.ar-cal-cell').forEach(cell => {
      cell.addEventListener('click', () => {
        const dateStr = (cell as HTMLElement).dataset.datestr;
        if (dateStr) {
          const parts = dateStr.split('-');
          const selectedDate = new Date(parseInt(parts[0], 10), parseInt(parts[1], 10) - 1, parseInt(parts[2], 10), 12, 0, 0);
          if (this.callbacks) this.callbacks.onDateChange(selectedDate);
          this.isCalendarOpen = false;
          if (this.calendarPopupEl) this.calendarPopupEl.style.display = 'none';
        }
      });
    });
  }
}
